import { Immutable } from "immer";
import React from "react";

import { SVG_DATA } from "./svgData";

type IconProps = Immutable<{
    name: string;
    className?: string;
    svgClassName?: string;
    /**
     * @deprecated Prefer `title`. Kept as an alias so existing call sites keep
     * typechecking; both map to Allyant `role="img"` + `<title>` labeling.
     */
    alt?: string;
    title?: string;
    width?: number;
    height?: number;
}>;

/**
 * Renders an inline SVG icon from a predefined set of icons.
 *
 * This component is designed for use in SSR components to prerender SVGs,
 * ensuring that icons are available without additional network requests or client-side rendering.
 *
 */
export const SVGIcon: React.FC<IconProps> = ({
    name,
    className = "",
    svgClassName,
    width,
    height,
    alt,
    title,
    ...restProps
}) => {
    const titleId = React.useId();
    const icon = SVG_DATA[name as keyof typeof SVG_DATA];
    const accessibleName = title ?? alt;

    if (!icon) {
        console.warn(`Icon "${name}" not found`);
        return null;
    }

    const svgProps = accessibleName
        ? {
              "className": svgClassName,
              "width": width,
              "height": height,
              "role": "img",
              "aria-labelledby": titleId,
              "children": (
                  <>
                      <title id={titleId}>{accessibleName}</title>
                      {icon.props.children}
                  </>
              ),
          }
        : {
              className: svgClassName,
              width,
              height,
          };

    return (
        <span className={`${className}`} {...restProps}>
            {React.cloneElement(icon, svgProps)}
        </span>
    );
};
